import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion, onAnimationEnd } from '@theme/utilities';

/**
 * Updates Build Your Set "Add to Set" button disabled state from confirmation checkbox.
 * Callable from document listeners (e.g. personalisation-saved); modalContent optional.
 * @param {Element} [modalContent] - #quick-add-modal-content, or omitted to resolve from document
 */
function updateBuildYourSetAddToSetState(modalContent) {
  const modal = modalContent || document.getElementById('quick-add-modal-content');
  if (!modal?.hasAttribute('data-build-your-set')) return;
  const btn = modal.querySelector('.build-your-set-add-to-session-button');
  if (!btn) return;
  const colorRequiredMsg = modal.querySelector('[data-personalise-color-required-msg]');
  const colorRequiredVisible = colorRequiredMsg && colorRequiredMsg.style.display !== 'none' && colorRequiredMsg.offsetParent !== null;
  if (colorRequiredVisible) {
    btn.disabled = true;
    return;
  }
  const confirmation = modal.querySelector('[data-personalise-confirmation]');
  const checkbox = confirmation?.querySelector('[data-personalise-confirm-checkbox]');
  if (!confirmation || !checkbox) {
    btn.disabled = false;
    return;
  }
  const isVisible = confirmation.offsetParent !== null ||
    confirmation.style.display !== 'none' ||
    window.getComputedStyle(confirmation).display !== 'none';
  if (!isVisible) {
    btn.disabled = false;
    return;
  }
  btn.disabled = !checkbox.checked;
}

window.updateBuildYourSetAddToSetState = updateBuildYourSetAddToSetState;

/**
 * Sets or clears party favours markers on the quick-add modal, including minimum quantity
 * from the Party Favours section (data-minimum-quantity) for cart line properties.
 * @param {HTMLElement | null | undefined} modalContent
 * @param {boolean} isFromPartyFavours
 * @param {import('./product-card').ProductCard | null} productCard
 * @param {HTMLElement} quickAddEl
 */
function setQuickAddModalPartyFavoursMarkers(modalContent, isFromPartyFavours, productCard, quickAddEl) {
  if (!modalContent) return;
  if (isFromPartyFavours) {
    modalContent.setAttribute('data-party-favours', 'true');
    const section =
      quickAddEl.closest('[data-testid="party-favours"]') ||
      productCard?.closest('[data-testid="party-favours"]');
    const rawMin = section?.dataset?.minimumQuantity;
    const parsed = parseInt(rawMin || '5', 10);
    const min = Number.isFinite(parsed) && parsed > 0 ? parsed : 5;
    modalContent.setAttribute('data-party-favours-min', String(min));
  } else {
    modalContent.removeAttribute('data-party-favours');
    modalContent.removeAttribute('data-party-favours-min');
  }
}

// Prefetch coordinator to warm quick-add content for visible cards.
const quickAddPrefetchQueue = new Set();
let quickAddPrefetchObserver = null;
let quickAddPrefetchScheduled = false;

function scheduleQuickAddPrefetch() {
  if (quickAddPrefetchScheduled) return;
  quickAddPrefetchScheduled = true;
  const run = () => {
    quickAddPrefetchScheduled = false;
    const items = Array.from(quickAddPrefetchQueue);
    quickAddPrefetchQueue.clear();
    items.forEach((component) => {
      try {
        component.prefetch();
      } catch (err) {
        // silent
      }
    });
  };
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(run, { timeout: 1500 });
  } else {
    setTimeout(run, 400);
  }
}

/**
 * Morphing does not run scripts; we re-inject inline handlers only for real JavaScript.
 * JSON-LD and other types (e.g. application/ld+json) throw SyntaxError ("Unexpected token ':'") if executed.
 * @param {HTMLScriptElement} script
 */
function isQuickAddExecutableInlineScript(script) {
  if (!(script instanceof HTMLScriptElement) || script.src) return false;
  const raw = script.getAttribute('type');
  if (raw == null) return true;
  const t = raw.trim().toLowerCase();
  if (t === '') return true;
  return (
    t === 'text/javascript' ||
    t === 'application/javascript' ||
    t === 'text/ecmascript' ||
    t === 'application/ecmascript' ||
    t === 'text/jscript'
  );
}

function registerQuickAddPrefetch(component) {
  if (!component) return;
  if (!quickAddPrefetchObserver) {
    quickAddPrefetchObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const target = entry.target;
        quickAddPrefetchObserver.unobserve(target);
        const comp = target instanceof HTMLElement ? target.closest('quick-add-component') : null;
        if (!comp) return;
        quickAddPrefetchQueue.add(comp);
        scheduleQuickAddPrefetch();
      });
    }, { rootMargin: '200px 0px', threshold: 0.01 });
  }

  try {
    quickAddPrefetchObserver.observe(component);
  } catch (err) {
    // ignore
  }
}

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<void>>} */
  #prefetchRequests = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    // Fallback: Try to find product link manually if getProductCardLink doesn't work
    let productUrl = productLink?.href || '';
    
    if (!productUrl) {
      // Try to find link with ref="productCardLink" or class="product-card__link"
      const cardElement = this.closest('product-card');
      if (cardElement) {
        const linkWithRef = cardElement.querySelector('a[ref="productCardLink"]');
        const linkWithClass = cardElement.querySelector('a.product-card__link');
        const anyProductLink = cardElement.querySelector('a[href*="/products/"]');
        productUrl = linkWithRef?.href || linkWithClass?.href || anyProductLink?.href || '';
      }
    }
    
    // Final fallback: Check data attributes on the quick-add component itself
    if (!productUrl) {
      productUrl = this.getAttribute('data-product-url') || '';
      if (!productUrl && this.getAttribute('data-product-handle')) {
        const handle = this.getAttribute('data-product-handle');
        productUrl = `/products/${handle}`;
      }
    }

    if (!productUrl) return '';

    const url = new URL(productUrl, window.location.origin);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    this.addEventListener('pointerenter', this.#handlePrefetchIntent, { passive: true });
    this.addEventListener('focusin', this.#handlePrefetchIntent);
    this.addEventListener('touchstart', this.#handlePrefetchIntent, { passive: true });
    registerQuickAddPrefetch(this);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.removeEventListener('pointerenter', this.#handlePrefetchIntent);
    this.removeEventListener('focusin', this.#handlePrefetchIntent);
    this.removeEventListener('touchstart', this.#handlePrefetchIntent);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    this.#prefetchRequests.clear();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
    this.#prefetchRequests.clear();
  };

  #handlePrefetchIntent = () => {
    void this.#prefetchCurrentProduct();
  };

  /**
   * Public prefetch method used by intersection observer.
   */
  prefetch() {
    void this.#prefetchCurrentProduct();
  }

  async #prefetchCurrentProduct() {
    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;
    if (this.#cachedContent.has(currentUrl)) return;
    if (this.#prefetchRequests.has(currentUrl)) return;

    const request = (async () => {
      try {
        // Prefetch uses a plain request; click flow keeps abortable fetch behavior.
        const html = await this.fetchProductPage(currentUrl, null);
        if (!html) return;

        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('Quick Add: Prefetch failed', error);
        }
      }
    })();

    this.#prefetchRequests.set(currentUrl, request);
    try {
      await request;
    } finally {
      this.#prefetchRequests.delete(currentUrl);
    }
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // Check if this quick-add is from build-your-set or party-favours and mark it
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const explicitBuildYourSet = this.getAttribute('data-build-your-set');
    const isFromBuildYourSet = explicitBuildYourSet === 'true'
      ? true
      : explicitBuildYourSet === 'false'
        ? false
        : productCard?.hasAttribute('data-build-your-set') === true ||
          Boolean(productCard?.closest('[data-testid="build-your-set"], .build-your-set-section'));
    const isFromPartyFavours = this.hasAttribute('data-party-favours') ||
                               productCard?.hasAttribute('data-party-favours') ||
                               Boolean(productCard?.closest('[data-testid="party-favours"], .party-favours-section'));
    
    // Mark the modal content element with a data attribute if from build-your-set or party-favours
    const modalContent = document.getElementById('quick-add-modal-content');
    if (modalContent) {
      if (isFromBuildYourSet) {
        modalContent.setAttribute('data-build-your-set', 'true');
      } else {
        modalContent.removeAttribute('data-build-your-set');
      }

      setQuickAddModalPartyFavoursMarkers(modalContent, isFromPartyFavours, productCard, this);
    }

    const currentUrl = this.productPageUrl;

    if (!currentUrl) {
      console.warn('Quick Add: Product page URL is empty');
      // Still try to open the modal with empty content
      this.#openQuickAddModal();
      return;
    }

    // Abort any previous fetch requests and reset state
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // Clear any stuck loader state from previous errors
    if (modalContent) {
      // Check if content is stuck showing loader (skeleton)
      const hasSkeleton = modalContent.querySelector('.quick-add-modal__loading-skeleton');
      if (hasSkeleton && modalContent.innerHTML.trim() === this.#createLoadingSkeleton().trim()) {
        // Content is stuck on loader, clear it
        modalContent.innerHTML = '';
      }
    }
    
    let productGrid = this.#cachedContent.get(currentUrl);

    // If we have cached content, use it immediately (no loader needed)
    if (productGrid && modalContent) {
      // Open modal first
      this.#openQuickAddModal();
      // Update with cached content immediately (no loader)
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      requestAnimationFrame(async () => {
        if (!this.#abortController?.signal.aborted) {
          try {
            await this.updateQuickAddModal(freshContent);
            // Clear abort controller after successful update (ready for next operation)
            if (this.#abortController) {
              this.#abortController = null;
            }
          } catch (error) {
            if (error.name !== 'AbortError') {
              console.error('Quick Add: Error updating modal with cached content', error);
            }
            // Clear abort controller even on error (unless it was aborted)
            if (this.#abortController && !this.#abortController.signal.aborted) {
              this.#abortController = null;
            }
          }
        }
      });
      return;
    }

    // No cached content - show loading skeleton
    if (modalContent) {
      modalContent.innerHTML = this.#createLoadingSkeleton();
    }

    // Open the modal immediately, then load content asynchronously
    this.#openQuickAddModal();

    // Load content asynchronously
    (async () => {
      try {
        if (!productGrid) {
          const prefetchRequest = this.#prefetchRequests.get(currentUrl);
          if (prefetchRequest) {
            await prefetchRequest;
            productGrid = this.#cachedContent.get(currentUrl);
          }
        }

        // Check if we have cached content for this URL (double check after fetch)
        if (!productGrid) {
          let timeoutId;
          try {
            // Create a timeout promise that can be cancelled
            const timeoutPromise = new Promise((_, reject) => {
              timeoutId = setTimeout(() => {
                if (!this.#abortController?.signal.aborted) {
                  reject(new Error('Fetch timeout'));
                }
              }, 10000);
            });

            // Fetch and cache the content with timeout
            const html = await Promise.race([
              this.fetchProductPage(currentUrl),
              timeoutPromise
            ]);
            
            // Clear timeout since one of the promises resolved
            if (timeoutId) {
              clearTimeout(timeoutId);
            }
            
            // Check if aborted before processing
            if (this.#abortController?.signal.aborted) {
              return;
            }
            
            if (html) {
              const gridElement = html.querySelector('[data-product-grid-content]');
              if (gridElement) {
                // Cache the cloned element to avoid modifying the original
                productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
                this.#cachedContent.set(currentUrl, productGrid);
              }
            }
          } catch (error) {
            // Clear timeout on error
            if (timeoutId) {
              clearTimeout(timeoutId);
            }
            if (error.name !== 'AbortError' && error.message !== 'Fetch timeout') {
              console.error('Quick Add: Error fetching product page', error);
            }
            return;
          }
        }

        // Check if this request was aborted (user clicked another product)
        if (this.#abortController?.signal.aborted) {
          return;
        }

        if (productGrid) {
          try {
            // Use a fresh clone from the cache
            const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
            await this.updateQuickAddModal(freshContent);
            // Clear abort controller after successful update (ready for next operation)
            if (this.#abortController) {
              this.#abortController = null;
            }
          } catch (error) {
            if (error.name !== 'AbortError') {
              console.error('Quick Add: Error updating modal content', error);
              // Clear loader if there was an error
              const modalContent = document.getElementById('quick-add-modal-content');
              if (modalContent && modalContent.querySelector('.quick-add-modal__loading-skeleton')) {
                modalContent.innerHTML = '<div style="padding: 2rem; text-align: center;">Error loading product. Please try again.</div>';
              }
            }
            // Clear abort controller even on error (unless it was aborted)
            if (this.#abortController && !this.#abortController.signal.aborted) {
              this.#abortController = null;
            }
          }
        } else {
          // No product grid found - clear loader and show error
          const modalContent = document.getElementById('quick-add-modal-content');
          if (modalContent && modalContent.querySelector('.quick-add-modal__loading-skeleton')) {
            modalContent.innerHTML = '<div style="padding: 2rem; text-align: center;">Product not found. Please try again.</div>';
          }
          // Clear abort controller
          if (this.#abortController && !this.#abortController.signal.aborted) {
            this.#abortController = null;
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Quick Add: Unexpected error loading content', error);
          // Clear loader on unexpected error
          const modalContent = document.getElementById('quick-add-modal-content');
          if (modalContent && modalContent.querySelector('.quick-add-modal__loading-skeleton')) {
            modalContent.innerHTML = '<div style="padding: 2rem; text-align: center;">Error loading product. Please try again.</div>';
          }
          // Clear abort controller
          if (this.#abortController && !this.#abortController.signal.aborted) {
            this.#abortController = null;
          }
        }
      }
    })();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    const handleClose = () => {
      this.toggleAttribute('stay-visible', false);
      // Clean up state when dialog closes
      this.#cleanupOnDialogClose();
    };

    dialogComponent.addEventListener(DialogCloseEvent.eventName, handleClose, {
      once: true,
    });
  }

  /**
   * Cleans up state when dialog closes (abort requests, reset content if needed)
   */
  #cleanupOnDialogClose() {
    // Clear personalization when dialog closes
    const modalContent = document.getElementById('quick-add-modal-content');
    if (modalContent) {
      const productFormComponent = modalContent.querySelector('product-form-component');
      const productId = productFormComponent?.dataset?.productId;
      if (productId) {
        const key = `personalisation_${String(productId)}`;
        sessionStorage.removeItem(key);
      }
    }
    
    // Abort any pending fetch requests
    if (this.#abortController && !this.#abortController.signal.aborted) {
      this.#abortController.abort();
    }
    // Reset abort controller so it can be reused
    this.#abortController = null;
    
    // Note: We don't clear the modal content here because the user might want to see
    // the last product they viewed when reopening. Only clear on new product selection.
  }

  #openQuickAddModal = () => {
    // Ensure modal content marker is set after modal opens (in case it wasn't set before)
    requestAnimationFrame(() => {
      const modalContent = document.getElementById('quick-add-modal-content');
      if (modalContent) {
        const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
        const explicitBuildYourSet = this.getAttribute('data-build-your-set');
        const isFromBuildYourSet = explicitBuildYourSet === 'true'
          ? true
          : explicitBuildYourSet === 'false'
            ? false
            : productCard?.hasAttribute('data-build-your-set') === true ||
              Boolean(productCard?.closest('[data-testid="build-your-set"], .build-your-set-section'));
        const isFromPartyFavours = this.hasAttribute('data-party-favours') ||
                                   productCard?.hasAttribute('data-party-favours') ||
                                   Boolean(productCard?.closest('[data-testid="party-favours"], .party-favours-section'));
        if (isFromBuildYourSet) {
          modalContent.setAttribute('data-build-your-set', 'true');
        } else {
          modalContent.removeAttribute('data-build-your-set');
        }
        setQuickAddModalPartyFavoursMarkers(modalContent, isFromPartyFavours, productCard, this);
      }
    });
    
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!dialogComponent) {
      console.warn('Quick Add: Dialog element not found');
      return;
    }

    // Check if it's a QuickAddDialog instance or the right custom element
    if (!(dialogComponent instanceof QuickAddDialog) && dialogComponent.tagName.toLowerCase() !== 'quick-add-dialog') {
      console.warn('Quick Add: Dialog element is not a QuickAddDialog instance');
      return;
    }

    // Function to actually open the dialog
    const openDialog = (/** @type {QuickAddDialog} */ dialog) => {
      if (!dialog || !dialog.refs || !dialog.refs.dialog) {
        console.warn('Quick Add: Dialog refs not ready');
        // Try again after a short delay
        setTimeout(() => {
          if (dialog && dialog.refs && dialog.refs.dialog) {
            this.#stayVisibleUntilDialogCloses(dialog);
            dialog.showDialog();
            // Ensure close button handler is attached after opening
            this.#ensureCloseButtonHandler(dialog);
          }
        }, 100);
        return;
      }

      this.#stayVisibleUntilDialogCloses(dialog);
      dialog.showDialog();
      // Ensure close button handler is attached after opening
      this.#ensureCloseButtonHandler(dialog);
    };

    // If the custom element hasn't been upgraded yet, wait for it
    if (!(dialogComponent instanceof QuickAddDialog)) {
      // Wait for custom element to be defined
      customElements.whenDefined('quick-add-dialog').then(() => {
        if (dialogComponent instanceof QuickAddDialog) {
          openDialog(dialogComponent);
        }
      }).catch((error) => {
        console.error('Quick Add: Error waiting for custom element', error);
      });
      return;
    }

    openDialog(dialogComponent);
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @param {AbortSignal | null | undefined} [signal] - undefined uses click abort controller, null disables signal
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl, signal = undefined) {
    if (!productPageUrl) return null;

    let requestSignal = signal;
    if (signal === undefined) {
      // Use the existing abort controller for click-driven requests.
      if (!this.#abortController) {
        this.#abortController = new AbortController();
      }
      requestSignal = this.#abortController.signal;
    }

    try {
      const fetchOptions = requestSignal ? { signal: requestSignal } : {};
      const response = await fetch(productPageUrl, fetchOptions);

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    }
  }

  /**
   * Creates a loading skeleton that matches the modal design
   * @returns {string} HTML string for the loading skeleton
   */
  #createLoadingSkeleton() {
    return `
      <div class="quick-add-modal__loading-skeleton product-information__grid">
        <div class="quick-add-modal__skeleton-media product-information__media">
          <div class="quick-add-modal__skeleton-image"></div>
        </div>
        <div class="quick-add-modal__skeleton-details product-details">
          <div class="quick-add-modal__skeleton-line quick-add-modal__skeleton-title"></div>
          <div class="quick-add-modal__skeleton-line quick-add-modal__skeleton-price"></div>
          <div class="quick-add-modal__skeleton-line quick-add-modal__skeleton-line--medium"></div>
          <div class="quick-add-modal__skeleton-line quick-add-modal__skeleton-line--small"></div>
          <div class="quick-add-modal__skeleton-variants">
            <div class="quick-add-modal__skeleton-variant"></div>
            <div class="quick-add-modal__skeleton-variant"></div>
            <div class="quick-add-modal__skeleton-variant"></div>
          </div>
          <div class="quick-add-modal__skeleton-button"></div>
        </div>
      </div>
    `;
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;
    
    // Preserve build-your-set and party-favours markers if they were set before content update
    const wasBuildYourSet = modalContent.hasAttribute('data-build-your-set');
    const wasPartyFavours = modalContent.hasAttribute('data-party-favours');
    const wasPartyFavoursMin = modalContent.getAttribute('data-party-favours-min');
    
    // If this is a build-your-set product, clear any existing personalisation data
    // Personalization is now read directly from form inputs, no storage needed

    // Check if the request was aborted before updating
    if (this.#abortController?.signal.aborted) {
      return;
    }

    // Keep content same as desktop - no mobile-specific restructuring
    // The CSS will handle the layout differences

    morph(modalContent, productGrid);
    
    // Restore markers after morphing
    if (wasBuildYourSet) {
      modalContent.setAttribute('data-build-your-set', 'true');
    } else {
      modalContent.removeAttribute('data-build-your-set');
    }
    if (wasPartyFavours) {
      modalContent.setAttribute('data-party-favours', 'true');
      if (wasPartyFavoursMin) {
        modalContent.setAttribute('data-party-favours-min', wasPartyFavoursMin);
      }
    } else {
      modalContent.removeAttribute('data-party-favours');
      modalContent.removeAttribute('data-party-favours-min');
    }

    this.#syncVariantSelection(modalContent);
    
    // Ensure close button handler is re-attached after content is loaded
    // Use multiple callbacks to ensure DOM is settled after morph
    requestAnimationFrame(() => {
      const dialogComponent = document.getElementById('quick-add-dialog');
      if (dialogComponent instanceof QuickAddDialog) {
        // Trigger updatedCallback to ensure close button handler is set up via refs
        dialogComponent.updatedCallback();
        // Also use our helper method to ensure close button handler
        this.#ensureCloseButtonHandler(dialogComponent);
        
        // Try again after a short delay to be sure
        setTimeout(() => {
          this.#ensureCloseButtonHandler(dialogComponent);
        }, 100);
      }

      // Add View Product button to image container
      this.#addViewProductButton(modalContent);
      
      // Replace add-to-cart button with custom button for build-your-set
      if (wasBuildYourSet) {
        this.#replaceAddToCartButtonForBuildYourSet(modalContent);
      }
    });
    
    // Ensure personalise button event listeners work with dynamically loaded content
    requestAnimationFrame(() => {
      this.#ensurePersonaliseButtonHandlers(modalContent);
      
      // Clear any existing personalization for this product when modal opens
      // This ensures a fresh state each time the popup is opened
      const productFormComponent = modalContent.querySelector('product-form-component');
      const productId = productFormComponent?.dataset?.productId;
      if (productId) {
        const key = `personalisation_${String(productId)}`;
        sessionStorage.removeItem(key);
      }

      // Update personalise button text after modal content is loaded
      // Use multiple retries to ensure buttons are in DOM and function is available
      const updateButtonWithRetry = (attempts = 0) => {
        // Check if function exists in the morphed content's scripts
        if (attempts === 0) {
          // Execute all scripts in the morphed content (morph doesn't execute scripts automatically)
          const scripts = modalContent.querySelectorAll('script');
          scripts.forEach((script) => {
            if (!script.textContent?.trim() || !isQuickAddExecutableInlineScript(script)) return;
            try {
              // Execute the script to define functions and run initialization code
              const newScript = document.createElement('script');
              newScript.textContent = script.textContent;
              document.head.appendChild(newScript);
              document.head.removeChild(newScript);
            } catch (e) {
              console.error('Quick Add: Error executing script:', e);
            }
          });
        }
        
        if (typeof window.updatePersonaliseButtonText === 'function') {
          window.updatePersonaliseButtonText();
          if (wasBuildYourSet) {
            updateBuildYourSetAddToSetState(modalContent);
          }
          
          // Check if buttons in quick-add modal were updated
          const quickAddButtons = modalContent.querySelectorAll('[data-personalise-button]');
          let anyButtonUpdated = false;
          quickAddButtons.forEach((button) => {
            const textSpan = button.querySelector('[data-personalise-text]');
            if (textSpan && textSpan.textContent === 'EDIT') {
              anyButtonUpdated = true;
            }
          });
          
          // If buttons exist but weren't updated, retry
          if (quickAddButtons.length > 0 && !anyButtonUpdated && attempts < 10) {
            setTimeout(() => updateButtonWithRetry(attempts + 1), 300);
          }
        } else if (attempts < 10) {
          // Function not defined yet, retry
          setTimeout(() => updateButtonWithRetry(attempts + 1), 300);
        }
      };
      
      setTimeout(() => updateButtonWithRetry(0), 300);
    });

    // Prevent media gallery flickering on variant update - update image smoothly
    requestAnimationFrame(() => {
      this.#preventMediaGalleryFlicker(modalContent);
    });
  }

  /**
   * Prevents media gallery flickering by ensuring smooth image updates
   * Similar to personalize modal - shows single static image without flicker
   * @param {Element} modalContent - The modal content element
   */
  #preventMediaGalleryFlicker(modalContent) {
    if (!modalContent) return;

    // Add data attribute to mark this as a quick-add modal media gallery
    const mediaGallery = modalContent.querySelector('media-gallery');
    if (mediaGallery) {
      mediaGallery.setAttribute('data-quick-add-modal', 'true');
    }

    // Find the first image element (the one that should be displayed)
    const findFirstImage = () => {
      return modalContent.querySelector(
        '.product-information__media slideshow-slide:first-child img, .product-information__media .product-media img, .product-information__media img'
      );
    };

    let firstImage = findFirstImage();
    
    // Disable any transitions/animations on images to prevent flicker
    if (firstImage) {
      firstImage.style.transition = 'none';
      firstImage.style.animation = 'none';
      firstImage.style.opacity = '1';
    }

    // Listen for variant updates within the modal to update image smoothly
    const dialog = modalContent.closest('dialog');
    if (!dialog) return;

    const handleVariantUpdate = (event) => {
      // Only handle variant updates within this quick add modal
      if (!modalContent.contains(event.target)) return;

      const newHtml = event.detail?.data?.html;
      if (!newHtml) return;

      // Find the new first image from the updated content
      const newMediaGallery = newHtml.querySelector('media-gallery');
      if (!newMediaGallery) return;

      const newFirstImage = newMediaGallery.querySelector(
        'slideshow-slide:first-child img, .product-media img, img'
      );
      
      if (!newFirstImage) return;

      // Get current first image (might have changed due to DOM updates)
      firstImage = findFirstImage();
      if (!firstImage) return;

      // Get the new image src
      const newImageSrc = newFirstImage.src || newFirstImage.getAttribute('src') || 
                         newFirstImage.getAttribute('srcset')?.split(' ')[0];
      
      if (!newImageSrc || firstImage.src === newImageSrc) return;

      // Update image instantly without fade (like personalize modal)
      // Remove any existing transition
      firstImage.style.transition = 'none';
      firstImage.style.opacity = '1';
      
      // Update image source immediately
      firstImage.src = newImageSrc;
      
      if (newFirstImage.srcset) {
        firstImage.srcset = newFirstImage.srcset;
      }
      if (newFirstImage.sizes) {
        firstImage.sizes = newFirstImage.sizes;
      }
      if (newFirstImage.alt) {
        firstImage.alt = newFirstImage.alt;
      }
    };

    // Listen for variant update events on the dialog (capture phase to run before media-gallery handler)
    dialog.addEventListener(ThemeEvents.variantUpdate, handleVariantUpdate, { 
      capture: true,
      passive: true
    });
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Ensures the close button handler is attached to the dialog
   * @param {QuickAddDialog} dialogComponent - The dialog component
   */
  #ensureCloseButtonHandler(dialogComponent) {
    if (!(dialogComponent instanceof QuickAddDialog)) return;
    
    // Try multiple ways to find the close button
    let closeButton = null;
    
    // Method 1: Try refs
    if (dialogComponent.refs && dialogComponent.refs.closeButton && !Array.isArray(dialogComponent.refs.closeButton)) {
      closeButton = dialogComponent.refs.closeButton;
    }
    
    // Method 2: Find by class or ref attribute
    if (!closeButton) {
      const dialog = dialogComponent.refs?.dialog || dialogComponent.querySelector('dialog');
      if (dialog) {
        closeButton = dialog.querySelector('.quick-add-modal__close') || 
                     dialog.querySelector('button[ref="closeButton"]') ||
                     dialog.querySelector('button.quick-add-modal__close');
      }
    }
    
    // Method 3: Direct query from dialogComponent
    if (!closeButton) {
      closeButton = dialogComponent.querySelector('.quick-add-modal__close') || 
                   dialogComponent.querySelector('button[ref="closeButton"]') ||
                   dialogComponent.querySelector('button.quick-add-modal__close');
    }
    
    if (closeButton instanceof HTMLElement) {
      // Remove any existing handler
      if (closeButton._closeHandler) {
        closeButton.removeEventListener('click', closeButton._closeHandler, { capture: true });
        closeButton.removeEventListener('click', closeButton._closeHandler);
        delete closeButton._closeHandler;
      }
      
      // Create a robust handler that definitely closes the dialog
      const clickHandler = (event) => {
        console.log('Close button clicked', event);
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        // Try to close the dialog
        try {
          if (dialogComponent && typeof dialogComponent.closeDialog === 'function') {
            dialogComponent.closeDialog();
          } else {
            // Fallback: close the dialog element directly
            const dialog = dialogComponent.refs?.dialog || dialogComponent.querySelector('dialog');
            if (dialog && dialog.open) {
              dialog.close();
            }
          }
        } catch (error) {
          console.error('Error closing dialog:', error);
          // Last resort: try to close the dialog element directly
          const dialog = dialogComponent.querySelector('dialog');
          if (dialog) {
            dialog.close();
          }
        }
      };
      
      // Attach handler in both capture and bubble phases to ensure it runs
      closeButton.addEventListener('click', clickHandler, { capture: true });
      closeButton.addEventListener('click', clickHandler);
      closeButton._closeHandler = clickHandler;
      
      // Ensure the on:click attribute is set for the component system
      if (!closeButton.hasAttribute('on:click')) {
        closeButton.setAttribute('on:click', '/closeDialog');
      }
      
      // Also set a data attribute to mark it as handled
      closeButton.dataset.closeHandlerAttached = 'true';
    } else {
      console.warn('Quick Add: Close button not found', dialogComponent);
    }
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

  /**
   * Adds View Product button to the image container
   * @param {Element} modalContent - The modal content element
   */
  #addViewProductButton(modalContent) {
    if (!modalContent || !this.productPageUrl) return;

    const mediaContainer = modalContent.querySelector('.product-information__media');
    if (!mediaContainer) return;

    // Check if button already exists
    let viewProductButton = mediaContainer.querySelector('.quick-add-modal__view-product-button');
    if (viewProductButton) {
      // Update href if it exists
      viewProductButton.href = this.productPageUrl;
      return;
    }

    // Create the button
    viewProductButton = document.createElement('a');
    viewProductButton.href = this.productPageUrl;
    viewProductButton.className = 'quick-add-modal__view-product-button';
    viewProductButton.setAttribute('aria-label', 'View Product');

    // Create icon container
    const iconContainer = document.createElement('span');
    iconContainer.className = 'quick-add-modal__view-product-button-icon';
    
    // Create eye icon SVG
    const iconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    iconSvg.setAttribute('width', '18');
    iconSvg.setAttribute('height', '18');
    iconSvg.setAttribute('viewBox', '0 0 24 24');
    iconSvg.setAttribute('fill', 'none');
    iconSvg.setAttribute('stroke', 'currentColor');
    iconSvg.setAttribute('stroke-width', '2');
    iconSvg.setAttribute('stroke-linecap', 'round');
    iconSvg.setAttribute('stroke-linejoin', 'round');
    
    const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path1.setAttribute('d', 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z');
    const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    circle.setAttribute('cx', '12');
    circle.setAttribute('cy', '12');
    circle.setAttribute('r', '3');
    
    iconSvg.appendChild(path1);
    iconSvg.appendChild(circle);
    iconContainer.appendChild(iconSvg);

    // Create text
    const text = document.createTextNode('View Product');

    // Assemble button
    viewProductButton.appendChild(iconContainer);
    viewProductButton.appendChild(text);

    // Add to media container
    mediaContainer.appendChild(viewProductButton);
  }

  /**
   * Collects all product information from the modal content
   * @param {Element} modalContent - The modal content element
   * @param {string} productId - The product ID
   * @param {string} variantId - The variant ID
   * @returns {Object} Product information object
   */
  #collectProductInformation(modalContent, productId, variantId) {
    const productData = {
      product_id: productId,
      variant_id: variantId
    };

    // Get product name/title
    const productTitle = modalContent.querySelector('.product-title-text, .view-product-title, h1, [data-product-title]');
    if (productTitle) {
      productData.product_name = productTitle.textContent?.trim() || '';
    }

    // Get product handle/URL
    const productLink = modalContent.querySelector('.view-product-title a, .product-header a, a[href*="/products/"]');
    if (productLink) {
      productData.product_url = productLink.href || '';
      const urlMatch = productLink.href?.match(/\/products\/([^/?]+)/);
      if (urlMatch) {
        productData.product_handle = urlMatch[1];
      }
    }
    
    // Fallback: Try to get URL from product-form-component
    if (!productData.product_url) {
      const productForm = modalContent.querySelector('product-form-component');
      if (productForm) {
        // Check for data-product-url first
        if (productForm.dataset.productUrl) {
          productData.product_url = productForm.dataset.productUrl;
        } else if (productForm.dataset.productHandle) {
          // Construct URL from handle
          productData.product_handle = productForm.dataset.productHandle;
          productData.product_url = `/products/${productData.product_handle}`;
        }
      }
    }
    
    // Fallback: Try to get URL from product card if not found in modal
    if (!productData.product_url) {
      const productCard = modalContent.closest('product-card') || document.querySelector(`product-card[data-product-id="${productId}"]`);
      if (productCard) {
        const cardLink = productCard.querySelector('a[href*="/products/"]');
        if (cardLink) {
          productData.product_url = cardLink.href;
          const urlMatch = cardLink.href?.match(/\/products\/([^/?]+)/);
          if (urlMatch) {
            productData.product_handle = urlMatch[1];
          }
        }
      }
    }
    
    // Last resort: Construct from product ID if we have it
    if (!productData.product_url && productId) {
      // We can't construct URL from just ID, but we can try to get handle from a product card
      const anyProductCard = document.querySelector(`product-card[data-product-id="${productId}"]`);
      if (anyProductCard) {
        const cardLink = anyProductCard.querySelector('a[href*="/products/"]');
        if (cardLink) {
          productData.product_url = cardLink.href;
          const urlMatch = cardLink.href?.match(/\/products\/([^/?]+)/);
          if (urlMatch) {
            productData.product_handle = urlMatch[1];
          }
        }
      }
    }

    // Get price information - get actual selling price (the price that will be added to cart)
    const priceElement = modalContent.querySelector('product-price');
    if (priceElement) {
      // Get the actual selling price (current price, not compare-at-price)
      // The selling price is the price without .compare-at-price class
      const sellingPriceElement = priceElement.querySelector('.price:not(.compare-at-price), .price-snippet .price:not(.compare-at-price)');
      if (sellingPriceElement) {
        productData.price = sellingPriceElement.textContent?.trim() || '';
      } else {
        // Fallback: get all price text and extract the selling price
        const allPrices = priceElement.querySelectorAll('.price');
        // Find the price that's not compare-at-price
        for (const priceEl of allPrices) {
          if (!priceEl.classList.contains('compare-at-price')) {
            productData.price = priceEl.textContent?.trim() || '';
            break;
          }
        }
        // If still no price, get all text content
        if (!productData.price) {
          productData.price = priceElement.textContent?.trim() || '';
        }
      }
      
      // Try to get numeric price value from selling price
      const priceMatch = productData.price.match(/[\d,]+\.?\d*/);
      if (priceMatch) {
        productData.price_value = parseFloat(priceMatch[0].replace(/,/g, ''));
      }
    }

    // Get variant information
    const variantPicker = modalContent.querySelector('variant-picker, variant-main-picker');
    if (variantPicker) {
      const selectedVariant = variantPicker.querySelector(`input[data-variant-id="${variantId}"], [data-variant-id="${variantId}"]`);
      if (selectedVariant) {
        productData.variant_title = selectedVariant.getAttribute('data-variant-title') || 
                                    selectedVariant.getAttribute('aria-label') || 
                                    selectedVariant.textContent?.trim() || '';
      }
    }

    // Get product images
    const images = [];
    const mediaGallery = modalContent.querySelector('media-gallery, slideshow-component');
    if (mediaGallery) {
      const imageElements = mediaGallery.querySelectorAll('img[src], img[data-src]');
      imageElements.forEach((img) => {
        const imageUrl = img.src || img.getAttribute('data-src') || img.getAttribute('srcset')?.split(' ')[0];
        if (imageUrl && !images.includes(imageUrl)) {
          images.push(imageUrl);
        }
      });
    }
    
    // Fallback: get images from product media containers
    if (images.length === 0) {
      const mediaContainers = modalContent.querySelectorAll('.product-media img, .product-information__media img');
      mediaContainers.forEach((img) => {
        const imageUrl = img.src || img.getAttribute('data-src') || img.getAttribute('srcset')?.split(' ')[0];
        if (imageUrl && !images.includes(imageUrl)) {
          images.push(imageUrl);
        }
      });
    }

    // Get featured image (first image)
    if (images.length > 0) {
      productData.featured_image = images[0];
      productData.images = images;
    }

    // Check if product supports personalization (has personalization button)
    const personaliseButton = modalContent.querySelector('[data-personalise-button]');
    productData.needs_personalization = personaliseButton !== null;
    
    // Collect personalization field structure (what fields are available for this product)
    productData.personalization_fields = null;
    if (productData.needs_personalization) {
      const personalizationFields = [];
      
      // Find the personalise modal/dialog - prefer the one inside modalContent (the product we're adding)
      const personaliseModalInContent = modalContent.querySelector('personalise-dialog');
      const personaliseModal = personaliseModalInContent || document.querySelector('personalise-dialog');
      const personaliseModalContent = personaliseModal?.querySelector('.personalise-modal') || 
                                      modalContent.querySelector('.personalise-modal') ||
                                      document.querySelector('.personalise-modal');
      
      if (personaliseModalContent) {
        // Collect all personalization input fields (including text, date, etc.)
        const allInputs = personaliseModalContent.querySelectorAll('input[name="personalise-name"], input[name^="properties["], input[type="date"], input[type="text"]:not([type="hidden"])');
        allInputs.forEach((input) => {
          // Skip hidden inputs and inputs that are part of radio/checkbox groups (handled separately)
          if (input.type === 'hidden' || input.type === 'radio' || input.type === 'checkbox') {
            return;
          }
          
          const fieldInfo = {
            name: input.name || '',
            type: input.type || 'text',
            maxlength: input.maxlength || null,
            placeholder: input.placeholder || '',
            required: input.hasAttribute('required') || input.hasAttribute('aria-required'),
            label: ''
          };
          
          // Try to find the label for this input
          const label = personaliseModalContent.querySelector(`label[for="${input.id}"]`) ||
                        input.closest('.personalise-modal__field')?.querySelector('.personalise-modal__label') ||
                        input.closest('.personalise-name-tabs__panel')?.querySelector('.personalise-name-tabs__panel-title');
          if (label) {
            fieldInfo.label = label.textContent?.trim() || '';
          }
          
          // Check for specific field types
          if (input.name === 'personalise-name') {
            fieldInfo.field_type = 'name';
            // Get maxlength from dynamic_max if available
            const charCounter = personaliseModalContent.querySelector('.personalise-modal__counter');
            if (charCounter) {
              const maxText = charCounter.textContent?.match(/\/(\d+)/);
              if (maxText) {
                fieldInfo.maxlength = parseInt(maxText[1], 10);
              }
            }
          } else if (input.name.includes("Baby's Name")) {
            fieldInfo.field_type = 'baby_name';
          } else if (input.name.includes("Kid's Name")) {
            fieldInfo.field_type = 'kid_name';
          } else if (input.name.includes("Mum's Name")) {
            fieldInfo.field_type = 'mum_name';
          } else if (input.name.includes('Name 1')) {
            fieldInfo.field_type = 'name1';
          } else if (input.name.includes('Name 2')) {
            fieldInfo.field_type = 'name2';
          } else if (input.name.includes('Name 3')) {
            fieldInfo.field_type = 'name3';
          } else if (input.name.includes('Name 4')) {
            fieldInfo.field_type = 'name4';
          } else if (input.name.includes('Date of Birth') || input.type === 'date') {
            fieldInfo.field_type = 'dob';
            fieldInfo.type = 'date';
            fieldInfo.pattern = input.pattern || '';
          } else if (input.name.includes('School Year')) {
            fieldInfo.field_type = 'school_year';
          } else if (input.name.includes('Time')) {
            fieldInfo.field_type = 'time';
          } else if (input.name.includes('Weight')) {
            fieldInfo.field_type = 'weight';
          } else if (input.name.includes('Personalisation:')) {
            fieldInfo.field_type = 'textbox';
          }
          
          personalizationFields.push(fieldInfo);
        });
        
        // Collect all textarea fields
        const textareas = personaliseModalContent.querySelectorAll('textarea[name^="properties["]');
        textareas.forEach((textarea) => {
          const fieldInfo = {
            name: textarea.name || '',
            type: 'textarea',
            maxlength: textarea.maxlength || null,
            placeholder: textarea.placeholder || '',
            required: textarea.hasAttribute('required') || textarea.hasAttribute('aria-required'),
            label: '',
            rows: textarea.rows || 3
          };
          
          // Try to find the label for this textarea
          const label = personaliseModalContent.querySelector(`label[for="${textarea.id}"]`) ||
                        textarea.closest('.personalise-modal__field')?.querySelector('.personalise-modal__label');
          if (label) {
            fieldInfo.label = label.textContent?.trim() || '';
          }
          
          if (textarea.name.includes('Personalisation:')) {
            fieldInfo.field_type = 'textbox';
          } else if (textarea.name.includes('Message')) {
            fieldInfo.field_type = 'message';
          }
          
          personalizationFields.push(fieldInfo);
        });
        
        // Collect color options if available
        const colorGrid = personaliseModalContent.querySelector('.personalise-modal__color-grid');
        if (colorGrid) {
          const colorOptions = [];
          const colorButtons = colorGrid.querySelectorAll('.personalise-modal__color-button, [data-color]');
          colorButtons.forEach((button) => {
            const colorValue = button.getAttribute('data-color') || 
                              button.querySelector('input[type="radio"]')?.value || '';
            if (colorValue) {
              colorOptions.push({
                value: colorValue,
                label: button.getAttribute('title') || colorValue,
                display: button.querySelector('.swatch')?.style.backgroundColor || colorValue
              });
            }
          });
          if (colorOptions.length > 0) {
            personalizationFields.push({
              field_type: 'text_color',
              name: 'personalise-color',
              type: 'radio',
              options: colorOptions,
              required: true,
              label: 'Choose Font Colour'
            });
          }
        }
        
        // Collect font options if available
        const fontGrid = personaliseModalContent.querySelector('.personalise-modal__font-grid');
        if (fontGrid) {
          const fontOptions = [];
          const fontButtons = fontGrid.querySelectorAll('.personalise-modal__font-button, [data-font]');
          fontButtons.forEach((button) => {
            const fontValue = button.getAttribute('data-font') || '';
            if (fontValue) {
              fontOptions.push({
                value: fontValue,
                label: fontValue,
                display: button.textContent?.trim() || fontValue
              });
            }
          });
          if (fontOptions.length > 0) {
            personalizationFields.push({
              field_type: 'font',
              name: 'personalise-font',
              type: 'select',
              options: fontOptions,
              required: true,
              label: 'Choose Font Style'
            });
          }
        }
      }
      
        if (personalizationFields.length > 0) {
          productData.personalization_fields = personalizationFields;
        }
      }
      
      // Note: Product tags will be fetched from JSON API when button is created
      // The personalise modal is not included in quick-add modal content, so we fetch from API
      productData.product_tags = null;
    
    // Collect personalizations from form inputs in the modal (current values)
    // Skip for Build Your Set to avoid auto-adding color without explicit save.
    productData.personalizations = null;
    if (!modalContent.hasAttribute('data-build-your-set')) {
      const personalizations = {};
      
      // Collect from personalise modal if it exists in the quick-add modal
      const personaliseModal = modalContent.querySelector('personalise-dialog') || 
                               document.querySelector('personalise-dialog');
      const personaliseModalContent = personaliseModal?.querySelector('.personalise-modal') || 
                                      modalContent.querySelector('.personalise-modal') ||
                                      document.querySelector('.personalise-modal');
      
      if (personaliseModalContent) {
        // Collect name input
        const nameInput = personaliseModalContent.querySelector('input[name="personalise-name"]');
        if (nameInput && nameInput.value) {
          personalizations['personalise-name'] = nameInput.value;
        }
        
        // Collect all properties inputs
        const propertyInputs = personaliseModalContent.querySelectorAll('input[name^="properties["]');
        propertyInputs.forEach((input) => {
          if (input.type === 'radio') {
            if (input.checked && input.value) {
              const propName = input.name.match(/properties\[(.+)\]/)?.[1];
              if (propName) {
                personalizations[`properties[${propName}]`] = input.value;
              }
            }
          } else if (input.type !== 'hidden' && input.value) {
            const propName = input.name.match(/properties\[(.+)\]/)?.[1];
            if (propName) {
              personalizations[`properties[${propName}]`] = input.value;
            }
          }
        });
        
        // Collect textareas
        const propertyTextareas = personaliseModalContent.querySelectorAll('textarea[name^="properties["]');
        propertyTextareas.forEach((textarea) => {
          if (textarea.value) {
            const propName = textarea.name.match(/properties\[(.+)\]/)?.[1];
            if (propName) {
              personalizations[`properties[${propName}]`] = textarea.value;
            }
          }
        });
        
        // Collect color selection (from radio buttons)
        const colorRadio = personaliseModalContent.querySelector('input[name="personalise-color"]:checked, input[type="radio"][name*="color"]:checked');
        if (colorRadio && colorRadio.value) {
          personalizations['personalise-color'] = colorRadio.value;
        }
        
        // Collect font selection (from hidden input or button data)
        const fontInput = personaliseModalContent.querySelector('input[name="personalise-font"], input[type="hidden"][name*="font"]');
        if (fontInput && fontInput.value) {
          personalizations['personalise-font'] = fontInput.value;
        } else {
          // Try to get from selected font button (uses --selected class, not .active)
          const selectedFontButton = personaliseModalContent.querySelector('.personalise-modal__font-button--selected, .personalise-modal__font-button[data-font].personalise-modal__font-button--selected');
          if (selectedFontButton) {
            const fontValue = selectedFontButton.getAttribute('data-font') || selectedFontButton.textContent?.trim();
            if (fontValue) {
              personalizations['personalise-font'] = fontValue;
            }
          }
          // Also check for active class as fallback
          if (!personalizations['personalise-font']) {
            const activeFontButton = personaliseModalContent.querySelector('.personalise-modal__font-button.active, [data-font].active');
            if (activeFontButton) {
              const fontValue = activeFontButton.getAttribute('data-font') || activeFontButton.textContent?.trim();
              if (fontValue) {
                personalizations['personalise-font'] = fontValue;
              }
            }
          }
        }
      }
      
      // Personalization is read directly from form inputs, no storage fallback needed
      
      if (Object.keys(personalizations).length > 0) {
        productData.personalizations = personalizations;
      }
    }

    // Get variant options/attributes
    const variantOptions = {};
    const optionInputs = modalContent.querySelectorAll('input[type="radio"][name^="options"], select[name^="options"]');
    optionInputs.forEach((input) => {
      if (input.checked || (input instanceof HTMLSelectElement && input.selectedIndex >= 0)) {
        const optionName = input.name.replace('options[', '').replace(']', '');
        const optionValue = input.value || (input instanceof HTMLSelectElement ? input.options[input.selectedIndex]?.text : input.getAttribute('data-option-value'));
        if (optionName && optionValue) {
          variantOptions[optionName] = optionValue;
        }
      }
    });
    if (Object.keys(variantOptions).length > 0) {
      productData.variant_options = variantOptions;
    }

    // Get product description if available
    const productDescription = modalContent.querySelector('.product-description, [data-product-description]');
    if (productDescription) {
      productData.product_description = productDescription.textContent?.trim() || '';
    }

    // Get SKU if available
    const skuElement = modalContent.querySelector('product-sku-component, [data-sku]');
    if (skuElement) {
      productData.sku = skuElement.textContent?.trim() || skuElement.getAttribute('data-sku') || '';
    }

    return productData;
  }

  /**
   * Replaces the default add-to-cart button with a custom button for build-your-set
   * @param {Element} modalContent - The modal content element
   */
  #replaceAddToCartButtonForBuildYourSet(modalContent) {
    if (!modalContent) return;
    
    // Reset stored variant when setting up (new product loaded)
    delete modalContent.dataset.currentVariantId;
    
    // Find the add-to-cart button(s) in the modal
    const addToCartButtons = modalContent.querySelectorAll('add-to-cart-component, [ref="addToCartButton"], button[type="submit"][name="add"]');
    const productForm = modalContent.querySelector('product-form-component');
    
    if (!productForm || addToCartButtons.length === 0) return;
    
    const productId = productForm.dataset.productId;
    
    // Find the form (from product-form-component - same form Add to Cart uses)
    const form = productForm.querySelector('form[data-type="add-to-cart-form"]');
    if (!form) return;
    // Reset saved-personalisation flag for this product's quick-add session
    form.dataset.personalisationSaved = 'false';
    
    // Hide all existing add-to-cart buttons
    addToCartButtons.forEach(button => {
      if (button instanceof HTMLElement) {
        button.style.display = 'none';
      }
    });
    
    // Find the button container (usually inside buy-buttons-block or product-form)
    const buyButtonsBlock = modalContent.querySelector('.buy-buttons-block');
    const buttonContainer = buyButtonsBlock || productForm;
    
    if (!buttonContainer) return;
    
    // Check if custom button already exists
    let customButton = buttonContainer.querySelector('.build-your-set-add-to-session-button');
    
    if (!customButton) {
      // Get variant ID from form (needed to collect product data)
      const variantIdInput = form.querySelector('input[name="id"][ref="variantId"]') ||
                            form.querySelector('input[name="id"]');
      let variantId = variantIdInput?.value;
      if (!variantId) {
        const variantPicker = modalContent.querySelector('variant-picker, variant-main-picker');
        const checkedOption = variantPicker?.querySelector('input[data-variant-id]:checked');
        variantId = checkedOption?.dataset?.variantId;
      }
      if (!variantId) {
        return;
      }
      
      // Collect all product information ONCE when button is created
      const productData = this.#collectProductInformation(modalContent, productId, variantId);
      productData.product_id = productId;
      productData.variant_id = variantId;
      
      // Ensure product_url is set - try from product-form-component if not already set
      if (!productData.product_url) {
        const productForm = modalContent.querySelector('product-form-component');
        if (productForm && productForm.dataset.productUrl) {
          productData.product_url = productForm.dataset.productUrl;
        } else if (productForm && productForm.dataset.productHandle) {
          productData.product_url = `/products/${productForm.dataset.productHandle}`;
          productData.product_handle = productForm.dataset.productHandle;
        }
      }
      
      // Fetch product tags from JSON API (personalise modal not in quick-add DOM)
      if (productData.product_url && productData.needs_personalization) {
        fetch(`${productData.product_url}.json`)
          .then(response => response.json())
          .then(data => {
            if (data.product && data.product.tags) {
              const tags = Array.isArray(data.product.tags) 
                ? data.product.tags 
                : data.product.tags.split(',').map(t => t.trim());
              productData.product_tags = tags;
              
              // Update button data attribute with tags
                const updatedProductDataJson = JSON.stringify(productData);
                customButton.setAttribute('data-product-data', updatedProductDataJson);
              }
            })
            .catch(() => {
              // Silently fail - tags will be fetched when needed
            });
      }
      
      // Store initial variant on modal (VariantUpdateEvent listener will update when user changes)
      modalContent.dataset.currentVariantId = variantId;
      
      // Listen for variant changes - disable Add to Set during change, enable when complete
      if (!modalContent.dataset.bysVariantListener) {
        modalContent.dataset.bysVariantListener = 'true';
        const dialog = modalContent.closest('dialog');
        if (dialog) {
          // Disable when user starts changing variant
          dialog.addEventListener(ThemeEvents.variantSelected, (e) => {
            if (!modalContent.contains(e.target)) return;
            const btn = modalContent.querySelector('.build-your-set-add-to-session-button');
            if (btn) {
              btn.disabled = true;
              // Safety: re-enable after 5s if VariantUpdateEvent never fires (e.g. fetch fails)
              setTimeout(() => {
                if (btn.disabled) {
                  btn.disabled = false;
                  updateBuildYourSetAddToSetState(modalContent);
                }
              }, 5000);
            }
          }, { capture: true });
          // Enable when variant change completes and store new variant
          dialog.addEventListener(ThemeEvents.variantUpdate, (e) => {
            if (!modalContent.contains(e.target)) return;
            const productIdMatch = !e.detail?.data?.productId || e.detail.data.productId === (modalContent.querySelector('product-form-component')?.dataset?.productId || '');
            if (productIdMatch && e.detail?.resource?.id) {
              const newVariantId = String(e.detail.resource.id);
              modalContent.dataset.currentVariantId = newVariantId;
              // Re-collect productData for the new variant so personalization_fields match (e.g. variant 2 has color C, not A/B)
              const btn = modalContent.querySelector('.build-your-set-add-to-session-button');
              if (btn) {
                requestAnimationFrame(() => {
                  const productForm = modalContent.querySelector('product-form-component');
                  const productId = productForm?.dataset?.productId;
                  if (productId) {
                    const freshProductData = this.#collectProductInformation(modalContent, productId, newVariantId);
                    const existingJson = btn.getAttribute('data-product-data');
                    if (existingJson) {
                      try {
                        const existing = JSON.parse(existingJson);
                        // Preserve product_tags (fetched async) and product_url/product_handle if fresh missed them
                        const merged = {
                          ...freshProductData,
                          product_tags: existing.product_tags || freshProductData.product_tags,
                          product_url: freshProductData.product_url || existing.product_url,
                          product_handle: freshProductData.product_handle || existing.product_handle
                        };
                        btn.setAttribute('data-product-data', JSON.stringify(merged));
                      } catch (_) {
                        btn.setAttribute('data-product-data', JSON.stringify(freshProductData));
                      }
                    }
                  }
                });
              }
            }
            const btn = modalContent.querySelector('.build-your-set-add-to-session-button');
            if (btn) {
              btn.disabled = false;
              updateBuildYourSetAddToSetState(modalContent); // Re-apply personalisation/confirmation state
            }
          }, { capture: true });
        }
      }
      
      // Store product data in button data attribute (tags will be updated async)
      const productDataJson = JSON.stringify(productData);
      
      // Create custom button with same styling as original (add-to-cart-button for shared disabled styles)
      customButton = document.createElement('button');
      customButton.type = 'button';
      customButton.className = 'button add-to-cart-button build-your-set-add-to-session-button';
      customButton.setAttribute('data-product-id', productId || '');
      customButton.setAttribute('data-product-data', productDataJson);
      
      // Get the text from the original button if available
      const originalButton = modalContent.querySelector('button[type="submit"][name="add"]');
      let buttonText = 'Add to Set';
    
      
      customButton.innerHTML = `
        <span class="add-to-cart-text">
          <span class="add-to-cart-text__content">
            <span>
              <span>${buttonText}</span>
            </span>
          </span>
        </span>
      `;
      
      // Add click handler - read from data attribute and collect fresh personalizations
      customButton.addEventListener('click', async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (customButton.disabled) return;

        // Get product data from button data attribute
        let productDataJson = customButton.getAttribute('data-product-data');
        if (!productDataJson) {
          return;
        }
        
        let productData = JSON.parse(productDataJson);
        
        // Get modal content fresh from DOM (may have changed since button creation)
        const currentModalContent = document.getElementById('quick-add-modal-content') || modalContent;
        
        // Prefer variant we captured from VariantUpdateEvent (most reliable - fires when user selects)
        let variantId = currentModalContent.dataset?.currentVariantId || productData.variant_id;
        
        // Fallback: read from form or variant picker (in case event didn't fire yet)
        const currentProductForm = currentModalContent.querySelector('product-form-component');
        const currentForm = currentProductForm?.querySelector('form[data-type="add-to-cart-form"]');
        if (!variantId && currentForm) {
          const formData = new FormData(currentForm);
          const formVariantId = formData.get('id');
          variantId = formVariantId ? String(formVariantId).trim() : currentForm.querySelector('input[name="id"]')?.value;
        }
        if (!variantId) {
          const variantPicker = currentModalContent.querySelector('variant-picker');
          const checkedOption = variantPicker?.querySelector('input[data-variant-id]:checked');
          variantId = checkedOption?.dataset?.variantId;
        }
        if (variantId) {
          productData.variant_id = variantId;
        }
        
        // Collect fresh price information at click time (may have changed with variant)
        const currentPriceElement = currentModalContent.querySelector('product-price');
        if (currentPriceElement) {
          // Get the actual selling price (current price, not compare-at-price)
          const sellingPriceElement = currentPriceElement.querySelector('.price:not(.compare-at-price), .price-snippet .price:not(.compare-at-price)');
          if (sellingPriceElement) {
            productData.price = sellingPriceElement.textContent?.trim() || '';
          } else {
            // Fallback: get all price text and extract the selling price
            const allPrices = currentPriceElement.querySelectorAll('.price');
            for (const priceEl of allPrices) {
              if (!priceEl.classList.contains('compare-at-price')) {
                productData.price = priceEl.textContent?.trim() || '';
                break;
              }
            }
            if (!productData.price) {
              productData.price = currentPriceElement.textContent?.trim() || '';
            }
          }
          
          // Update numeric price value
          const priceMatch = productData.price.match(/[\d,]+\.?\d*/);
          if (priceMatch) {
            productData.price_value = parseFloat(priceMatch[0].replace(/,/g, ''));
          }
        }
        
        // Build Your Set: only collect SAVED personalisations (from form).
        // The form gets populated when user clicks Save in the personalise modal.
        // Do NOT read from the personalise modal DOM – that would capture:
        // - auto-selected color when variant changes (user never opened modal)
        // - draft selections when user closed modal without saving
        const personalizations = {};
        const form = currentModalContent.querySelector('form[data-type="add-to-cart-form"]');
        const hasSavedPersonalisation = form?.dataset?.personalisationSaved === 'true';
        if (form && hasSavedPersonalisation) {
          // Check for personalise-name in form
          const formNameInput = form.querySelector('input[name="personalise-name"]');
          if (formNameInput && formNameInput.value) {
            personalizations['personalise-name'] = formNameInput.value;
          }
          if (Object.keys(personalizations).length === 0) {
            const namePropInput = form.querySelector('input[name="properties[Name]"]');
            if (namePropInput && namePropInput.value) {
              personalizations['personalise-name'] = namePropInput.value;
            }
          }
          // Check for properties in form (Save personalisation writes to hidden/radio inputs)
          const formPropertyInputs = form.querySelectorAll('input[name^="properties["], textarea[name^="properties["]');
          formPropertyInputs.forEach((input) => {
            if (input.type === 'radio') {
              if (input.checked && input.value) {
                const propName = input.name.match(/properties\[(.+)\]/)?.[1];
                if (propName) {
                  personalizations[`properties[${propName}]`] = input.value;
                }
              }
            } else if (input.value) {
              const propName = input.name.match(/properties\[(.+)\]/)?.[1];
              if (propName) {
                personalizations[`properties[${propName}]`] = input.value;
              }
            }
          });
        }
        
        // Normalise keys to match Build Your Set modal (so Edit pre-fills all fields)
        if (personalizations['properties[Name]']) {
          personalizations['personalise-name'] = personalizations['properties[Name]'];
          delete personalizations['properties[Name]'];
        }
        if (personalizations['properties[Text Font]']) {
          personalizations['personalise-font'] = personalizations['properties[Text Font]'];
          delete personalizations['properties[Text Font]'];
        }
        if (personalizations['properties[Text Colour]']) {
          personalizations['personalise-color'] = personalizations['properties[Text Colour]'];
          delete personalizations['properties[Text Colour]'];
        }
        if (personalizations['properties[Personalise Date of Birth]']) {
          personalizations['optionalDob'] = personalizations['properties[Personalise Date of Birth]'];
          delete personalizations['properties[Personalise Date of Birth]'];
        }
        
        // If personalizations found, update productData
        if (Object.keys(personalizations).length > 0) {
          productData.personalizations = personalizations;
        } else {
          delete productData.personalizations;
        }
        // Personalization is read directly from form inputs, no storage fallback needed

        // Capture gift wrap selections + gift message from form (for Build Your Set add-all)
        if (form) {
          const giftWrapCheckbox = form.querySelector('.chk_add_gift');
          const christmasGiftCheckbox = form.querySelector('.christmas-chk_add_gift');
          const giftMessageInput = form.querySelector('[data-gift-message-input]');
          const giftMessage = giftMessageInput?.value?.trim() || '';
          const giftWrapChecked = !!(giftWrapCheckbox?.checked && giftWrapCheckbox.value);
          const christmasChecked = !!(christmasGiftCheckbox?.checked && christmasGiftCheckbox.value);
          const giftWrapVariantId = giftWrapCheckbox?.value || '';
          const christmasVariantId = christmasGiftCheckbox?.value || '';
          if (giftWrapChecked || christmasChecked || giftMessage) {
            productData.gift_wrap = {
              giftWrapChecked,
              giftWrapVariantId,
              christmasChecked,
              christmasVariantId,
              giftMessage
            };
          }
        }
        
        // Prepare tag fetching function (will run in background after saving)
        const fetchTagsAndUpdate = () => {
          // Try to construct product URL if not available
          if (!productData.product_url && productData.product_handle) {
            productData.product_url = `/products/${productData.product_handle}`;
          }
          
          // Also try to get from product-form-component
          if (!productData.product_url) {
            const productForm = modalContent.querySelector('product-form-component');
            if (productForm && productForm.dataset.productUrl) {
              productData.product_url = productForm.dataset.productUrl;
            } else if (productForm && productForm.dataset.productHandle) {
              productData.product_url = `/products/${productForm.dataset.productHandle}`;
              productData.product_handle = productForm.dataset.productHandle;
            }
          }
          
          if (productData.needs_personalization && !productData.product_tags) {
            let tagsUrl = null;
            
            // Try to construct URL from available data
            if (productData.product_url) {
              // Ensure URL is absolute
              tagsUrl = productData.product_url;
              if (!tagsUrl.startsWith('http')) {
                tagsUrl = new URL(tagsUrl, window.location.origin).toString();
              }
            } else if (productData.product_handle) {
              // Construct from handle
              tagsUrl = `${window.location.origin}/products/${productData.product_handle}`;
            } else if (productId) {
              // Last resort: try to find product card and get handle from there
              const productCard = document.querySelector(`product-card[data-product-id="${productId}"]`);
              if (productCard) {
                const cardLink = productCard.querySelector('a[href*="/products/"]');
                if (cardLink) {
                  const urlMatch = cardLink.href.match(/\/products\/([^/?]+)/);
                  if (urlMatch) {
                    productData.product_handle = urlMatch[1];
                    tagsUrl = `${window.location.origin}/products/${productData.product_handle}`;
                  }
                }
              }
            }
            
            if (tagsUrl) {
              // Remove query parameters and hash before adding .json
              try {
                const urlObj = new URL(tagsUrl);
                // Remove query parameters and hash
                urlObj.search = '';
                urlObj.hash = '';
                // Add .json extension
                tagsUrl = urlObj.toString() + '.json';
              } catch (e) {
                // If URL parsing fails, try simple string manipulation
                const urlWithoutQuery = tagsUrl.split('?')[0].split('#')[0];
                tagsUrl = urlWithoutQuery + '.json';
              }
              
              // Fetch tags asynchronously (non-blocking) - no await, runs in background
              fetch(tagsUrl)
                .then(response => {
                  if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                  }
                  return response.json();
                })
                .then(data => {
                  if (data.product && data.product.tags) {
                    const tags = Array.isArray(data.product.tags) 
                      ? data.product.tags 
                      : data.product.tags.split(',').map(t => t.trim());
                    
                    // Update session storage with tags
                    const storageKey = 'build-your-set-session-cart';
                    try {
                      const stored = sessionStorage.getItem(storageKey);
                      if (stored) {
                        const sessionCart = JSON.parse(stored);
                        const existingIndex = sessionCart.findIndex(item => item.variant_id === variantId);
                        if (existingIndex >= 0) {
                          sessionCart[existingIndex].product_tags = tags;
                          sessionStorage.setItem(storageKey, JSON.stringify(sessionCart));
                          // Update button attribute with tags for future clicks
                          productData.product_tags = tags;
                          customButton.setAttribute('data-product-data', JSON.stringify(productData));
                          // Dispatch event to update sticky bar
                          document.dispatchEvent(new CustomEvent('build-your-set-updated', {
                            bubbles: true,
                            cancelable: true,
                            detail: { productCount: sessionCart.length }
                          }));
                        }
                      }
                    } catch (error) {
                      // Silently fail
                    }
                  }
                })
                .catch(() => {
                  // Silently fail - tags will be fetched when needed
                });
            }
          }
        };
        
        // Get quantity from form (use currentForm which reflects latest DOM)
        const quantityInput = currentForm?.querySelector('input[name="quantity"]');
        const quantity = quantityInput ? Number(quantityInput.value) || 1 : 1;
        const storageKey = 'build-your-set-session-cart';
        let sessionCart = [];
        try {
          const stored = sessionStorage.getItem(storageKey);
          if (stored) {
            sessionCart = JSON.parse(stored);
          }
        } catch (error) {
          sessionCart = [];
        }
        
        // Always add as a new entry — the same variant can appear multiple times
        // (e.g. same variant with different personalizations, or simply buying multiples)
        const newProduct = {
          ...productData,
          variant_id: variantId,
          product_id: productData.product_id,
          quantity,
          added_at: Date.now()
        };
        sessionCart.push(newProduct);
        
        try {
          sessionStorage.setItem(storageKey, JSON.stringify(sessionCart));
          
          // Dispatch event to update sticky bar immediately
          document.dispatchEvent(new CustomEvent('build-your-set-updated', {
            bubbles: true,
            cancelable: true,
            detail: { productCount: sessionCart.length }
          }));
          
          // Fetch tags in background (non-blocking) and update session when ready
          fetchTagsAndUpdate();
          
          // Close the modal
          const quickAddDialog = document.getElementById('quick-add-dialog');
          if (quickAddDialog && typeof quickAddDialog.closeDialog === 'function') {
            quickAddDialog.closeDialog();
          }
        } catch (error) {
          // Silently fail - error handling not critical for UX
        }
      });
      
      // Insert the button in the same position as the original
      const firstAddToCart = Array.from(addToCartButtons)[0];
      if (firstAddToCart && firstAddToCart.parentElement) {
        firstAddToCart.parentElement.insertBefore(customButton, firstAddToCart);
      } else {
        buttonContainer.appendChild(customButton);
      }
    }
    
    // Show the custom button
    if (customButton instanceof HTMLElement) {
      customButton.style.display = '';
    }

    // Build Your Set: wire confirmation checkbox → Add to Set disabled state
    this.#updateBuildYourSetAddToSetButtonState(modalContent);
    if (!modalContent.dataset.bysConfirmListener) {
      modalContent.dataset.bysConfirmListener = 'true';
      modalContent.addEventListener('change', (e) => {
        if (e.target?.matches?.('[data-personalise-confirm-checkbox]')) {
          updateBuildYourSetAddToSetState(modalContent);
        }
      });
    }

    // Build Your Set: update Add to Set state after personalisation-saved (once per page)
    if (!document.body.dataset.bysPersonalisationSavedListener) {
      document.body.dataset.bysPersonalisationSavedListener = 'true';
      document.addEventListener('personalisation-saved', (event) => {
        // Mark the relevant form as having saved personalisation
        const detailForm = event?.detail?.form;
        if (detailForm instanceof HTMLFormElement) {
          detailForm.dataset.personalisationSaved = 'true';
        }

        const formId = event?.detail?.formId;
        if (formId) {
          const formById = document.getElementById(formId);
          if (formById instanceof HTMLFormElement) {
            formById.dataset.personalisationSaved = 'true';
          }
        }

        const pid = event?.detail?.productId;
        if (pid) {
          const modalContent = document.getElementById('quick-add-modal-content');
          const formComponent = modalContent?.querySelector(`product-form-component[data-product-id="${pid}"]`);
          const form = formComponent?.querySelector('form[data-type="add-to-cart-form"]');
          if (form) form.dataset.personalisationSaved = 'true';
        }
        setTimeout(updateBuildYourSetAddToSetState, 200);
      });
    }
  }

  /**
   * Updates Build Your Set "Add to Set" button disabled state based on personalisation confirmation.
   * @param {Element} [modalContent] - #quick-add-modal-content
   */
  #updateBuildYourSetAddToSetButtonState(modalContent) {
    updateBuildYourSetAddToSetState(modalContent);
  }

  /**
   * Ensures personalise button event handlers work with dynamically loaded content
   * @param {Element} modalContent - The modal content element
   */
  #ensurePersonaliseButtonHandlers(modalContent) {
    // Build Your Set only: remove previous product's personalise-dialogs from body before processing.
    // Prevents "Personalised: {value}" from showing for a different product when reopening Quick Add.
    if (modalContent.hasAttribute('data-build-your-set')) {
      document.querySelectorAll('personalise-dialog[data-quick-add="true"]').forEach((el) => el.remove());
    }

    // CRITICAL: Always check for personalise-dialog in the modal content first
    // This ensures we use the dialog for the current product in quick-add, not the main page one
    let personaliseModal = modalContent.querySelector('personalise-dialog');
    
    // If found in modal content, move it to body (if not already there) and mark it for quick-add
    if (personaliseModal && personaliseModal.parentElement !== document.body) {
      // Mark it as being from quick-add so we can identify it later
      personaliseModal.setAttribute('data-quick-add', 'true');
      
      // Get current product ID to associate with this dialog
      const productFormComponent = modalContent.querySelector('product-form-component');
      const productId = productFormComponent?.dataset?.productId;
      if (productId) {
        personaliseModal.setAttribute('data-product-id', productId);
      }
      
      // Move it to the document body so it's accessible globally
      document.body.appendChild(personaliseModal);
      
      // CRITICAL: Ensure custom element is properly defined and initialized
      // When content is loaded via morph, the script might not have run yet
      customElements.whenDefined('personalise-dialog').then(() => {
        console.log('PersonaliseDialog: Custom element defined');
        
        // Force re-initialization by temporarily disconnecting and reconnecting
        // This ensures connectedCallback runs and sets up all handlers and functionality
        const parent = personaliseModal.parentNode;
        if (parent) {
          console.log('PersonaliseDialog: Re-initializing element after move to body');
          parent.removeChild(personaliseModal);
          document.body.appendChild(personaliseModal);
          
          // The element should now be properly initialized with all its methods
          console.log('PersonaliseDialog: Element re-initialized, methods available:', {
            showDialog: typeof personaliseModal.showDialog === 'function',
            closePersonaliseOnly: typeof personaliseModal.closePersonaliseOnly === 'function'
          });
        }
      }).catch((error) => {
        console.error('PersonaliseDialog: Error waiting for custom element definition:', error);
      });
    } else if (!personaliseModal) {
      // If not found in modal content, check if there's one already on the page
      // But mark it for quick-add use
      personaliseModal = document.querySelector('personalise-dialog[data-quick-add="true"]') ||
                        document.querySelector('personalise-dialog:not([data-context="cart-drawer"])');
      
      // Update product ID association if we found an existing one
      if (personaliseModal) {
        const productFormComponent = modalContent.querySelector('product-form-component');
        const productId = productFormComponent?.dataset?.productId;
        if (productId) {
          personaliseModal.setAttribute('data-product-id', productId);
          personaliseModal.setAttribute('data-quick-add', 'true');
        }
      }
    }

    // Ensure the personalise modal script is loaded and event listeners are set up
    // The event listener should already be set up globally via the script in buy-buttons.liquid
    // But we need to ensure it works with dynamically loaded content
    // Since event delegation is used (listening on document), it should work automatically
    // However, we need to make sure the personalise modal is available when the button is clicked
    
    // Find all personalise buttons in the modal content and ensure they work
    const personaliseButtons = modalContent.querySelectorAll('[data-personalise-button]');
    personaliseButtons.forEach(button => {
      // The global event listener should handle this, but we can add a direct handler as fallback
      if (!button.dataset.personaliseHandlerAttached) {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          // CRITICAL: Find personalise dialog within the quick-add modal content first
          // This ensures we use the dialog for the current product, not the main page product
          let dialog = modalContent.querySelector('personalise-dialog');
          
          // If not found in modal content, it might have been moved to body
          // In that case, we need to ensure we're using the right one
          // For quick-add, we should prioritize finding one that's not the main product page one
          if (!dialog) {
            const allDialogs = document.querySelectorAll('personalise-dialog');
            // If there are multiple dialogs, try to find one that's not the main product page one
            if (allDialogs.length > 1) {
              dialog = Array.from(allDialogs).find(d => {
                const context = d.getAttribute('data-context');
                return context !== 'product-page';
              }) || allDialogs[0];
            } else if (allDialogs.length === 1) {
              dialog = allDialogs[0];
            }
          }
          
          if (dialog && typeof dialog.showDialog === 'function') {
            dialog.showDialog();
          } else if (dialog) {
            // Fallback: try to open the dialog directly
            const dialogElement = dialog.querySelector('dialog');
            if (dialogElement) {
              dialogElement.showModal();
            }
          }
        });
        button.dataset.personaliseHandlerAttached = 'true';
      }
    });
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();
  #previousScrollY = 0;
  /** @type {'html' | 'body' | null} */
  #scrollLockKind = null;

  #releaseScrollLock() {
    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    this.#scrollLockKind = null;
  }

  connectedCallback() {
    super.connectedCallback();

    // Ensure we have the correct dialog ref - find the quick-add-modal dialog
    if (!this.refs?.dialog || !this.refs.dialog.classList.contains('quick-add-modal')) {
      const quickAddDialog = this.querySelector('dialog.quick-add-modal');
      if (quickAddDialog && this.refs) {
        this.refs.dialog = quickAddDialog;
      }
    }

    // Ensure close button has the event handler
    this.#ensureCloseButtonHandler();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  updatedCallback() {
    super.updatedCallback();
    
    // Ensure we have the correct dialog ref - find the quick-add-modal dialog
    if (!this.refs?.dialog || !this.refs.dialog.classList.contains('quick-add-modal')) {
      const quickAddDialog = this.querySelector('dialog.quick-add-modal');
      if (quickAddDialog && this.refs) {
        this.refs.dialog = quickAddDialog;
      }
    }
    
    // Ensure close button handler is set up after updates
    this.#ensureCloseButtonHandler();
  }

  /**
   * Override showDialog to ensure close button handler is attached when dialog opens
   * Also prevents closing on outside clicks - only closes via close button
   */
  showDialog() {
    const { dialog } = this.refs;

    // Ensure we have the correct dialog ref before showing
    const quickAddDialog = this.querySelector('dialog.quick-add-modal');
    if (quickAddDialog && this.refs) {
      this.refs.dialog = quickAddDialog;
    }

    // Use the refs dialog if available, otherwise use the found one
    const dialogToShow = this.refs?.dialog || quickAddDialog || dialog;
    if (!dialogToShow) return;

    if (dialogToShow.open) return;

    // Store scroll position before opening
    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      const isIOS = getIOSVersion() != null;
      this.#scrollLockKind = isIOS ? 'html' : 'body';
      if (isIOS) {
        document.documentElement.setAttribute('scroll-lock', '');
      } else {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
      }

      dialogToShow.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      // Only attach keydown listener for Escape key, NOT click listener for outside clicks
      // This prevents the dialog from closing when clicking outside
      this.addEventListener('keydown', this.#handleKeyDown);
    });

    // Ensure close button handler is attached after dialog is shown
    requestAnimationFrame(() => {
      this.#ensureCloseButtonHandler();
      // Try again after a short delay to be sure
      setTimeout(() => {
        this.#ensureCloseButtonHandler();
      }, 50);
    });
  }

  /**
   * Handle Escape key to close dialog
   * @param {KeyboardEvent} event - The keyboard event
   * @private
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Clear personalization for the product in the quick add modal
   * @private
   */
  #clearQuickAddPersonalisation() {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    // Find the product ID from the modal content
    const productFormComponent = modalContent.querySelector('product-form-component');
    const productId = productFormComponent?.dataset?.productId;

    if (productId) {
      const key = `personalisation_${String(productId)}`;
      sessionStorage.removeItem(key);
      
      // Also update button text to reset it
      if (typeof window.updatePersonaliseButtonText === 'function') {
        window.updatePersonaliseButtonText();
      }
    }
  }

  /**
   * Override closeDialog to ensure we're closing the correct dialog
   * Since parent's closeDialog is an arrow function (instance property), we implement it directly
   */
  closeDialog = async () => {
    // CRITICAL: Don't close if a personalise dialog is currently open
    // This prevents closing parent dialog when closing personalise popup
    const personaliseDialogElement = document.querySelector('personalise-dialog');
    if (personaliseDialogElement) {
      const personaliseNativeDialog = personaliseDialogElement.refs?.dialog || 
                                      personaliseDialogElement.querySelector('dialog');
      if (personaliseNativeDialog && personaliseNativeDialog.open) {
        // Personalise dialog is open, don't close quick-add dialog
        return;
      }
    }

    // Clear personalization when closing the quick add popup
    this.#clearQuickAddPersonalisation();

    // Get the correct dialog - the quick-add-modal, not personalise-modal
    let dialog = this.querySelector('dialog.quick-add-modal');
    
    // If we found the correct dialog, update refs
    if (dialog && this.refs) {
      this.refs.dialog = dialog;
    } else {
      // Fallback to refs
      dialog = this.refs?.dialog;
    }
    
    if (!dialog) {
      console.warn('QuickAddDialog: No dialog found to close');
      // Still reset body styles in case they're stuck (only if personalise dialog is also closed)
      const personaliseDialog = document.querySelector('personalise-dialog');
      const personaliseIsOpen = personaliseDialog && 
                                 (personaliseDialog.refs?.dialog?.open || 
                                  personaliseDialog.querySelector('dialog')?.open);
      if (!personaliseIsOpen) {
        this.#releaseScrollLock();
      }
      return;
    }
    
    // Verify it's the quick-add-modal
    if (!dialog.classList.contains('quick-add-modal')) {
      console.warn('QuickAddDialog: Dialog is not quick-add-modal, finding correct one');
      const quickAddDialog = this.querySelector('dialog.quick-add-modal');
      if (!quickAddDialog) {
        console.error('QuickAddDialog: Cannot find quick-add-modal dialog');
        // Reset styles anyway
        this.#releaseScrollLock();
        return;
      }
      // Update refs to point to correct dialog
      if (this.refs) {
        this.refs.dialog = quickAddDialog;
      }
      dialog = quickAddDialog;
    }

    if (!dialog.open) {
      // Still reset body styles in case they're stuck (even if dialog appears closed)
      this.#releaseScrollLock();
      return;
    }

    // Store original refs and ensure dialog ref is correct
    const originalRefsDialog = this.refs?.dialog;
    if (this.refs) {
      this.refs.dialog = dialog;
    }
    
    // Remove event listeners before closing
    this.removeEventListener('keydown', this.#handleKeyDown);
    
    dialog.classList.add('dialog-closing');

    try {
      await onAnimationEnd(dialog, undefined, { subtree: false });
    } catch (error) {
      // Ignore animation errors - continue with closing
      console.warn('QuickAddDialog: Animation end error (ignored):', error);
    }

    // Check if personalise dialog is still open - if so, don't reset body styles
    // This fixes the overlay issue where body styles weren't being reset properly
    const personaliseDialog = document.querySelector('personalise-dialog');
    const personaliseIsOpen = personaliseDialog && 
                               (personaliseDialog.refs?.dialog?.open || 
                                personaliseDialog.querySelector('dialog')?.open);
    
    // Only reset body styles if personalise dialog is also closed
    if (!personaliseIsOpen) {
      const shouldRestoreScroll = this.#scrollLockKind === 'body';
      this.#releaseScrollLock();
      if (shouldRestoreScroll) {
        window.scrollTo({ top: this.#previousScrollY || 0, behavior: 'instant' });
      }
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
    
    // Restore original refs if it was different (though we want to keep the correct one)
    // Actually, keep the correct dialog ref instead of restoring potentially wrong one
    if (this.refs && originalRefsDialog && originalRefsDialog !== dialog && originalRefsDialog.classList?.contains('quick-add-modal')) {
      // Only restore if the original was also a valid quick-add-modal
      this.refs.dialog = originalRefsDialog;
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clean up close button event listener
    // Try multiple ways to find the button
    let closeButton = this.refs?.closeButton;
    if (!closeButton || Array.isArray(closeButton)) {
      const dialog = this.refs?.dialog || this.querySelector('dialog');
      if (dialog) {
        closeButton = dialog.querySelector('.quick-add-modal__close') || 
                     dialog.querySelector('button[ref="closeButton"]');
      }
    }
    
    if (closeButton && !Array.isArray(closeButton)) {
      const button = /** @type {HTMLElement & { _closeHandler?: (event: MouseEvent) => void }} */ (closeButton);
      if (button._closeHandler) {
        // Remove from both capture and bubble phases
        button.removeEventListener('click', button._closeHandler, { capture: true });
        button.removeEventListener('click', button._closeHandler);
        delete button._closeHandler;
        delete button.dataset.closeHandlerAttached;
      }
    }

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Ensures the close button has the proper event handler
   */
  #ensureCloseButtonHandler() {
    // Try multiple ways to find the close button
    let closeButton = null;
    
    // Method 1: Try refs
    if (this.refs && this.refs.closeButton && !Array.isArray(this.refs.closeButton)) {
      closeButton = this.refs.closeButton;
    }
    
    // Method 2: Find by selector as fallback
    if (!closeButton) {
      const dialog = this.refs?.dialog || this.querySelector('dialog');
      if (dialog) {
        closeButton = dialog.querySelector('.quick-add-modal__close') || 
                     dialog.querySelector('button[ref="closeButton"]') ||
                     dialog.querySelector('button.quick-add-modal__close');
      }
    }
    
    // Method 3: Direct query
    if (!closeButton) {
      closeButton = this.querySelector('.quick-add-modal__close') || 
                   this.querySelector('button[ref="closeButton"]') ||
                   this.querySelector('button.quick-add-modal__close');
    }
    
    if (!closeButton || !(closeButton instanceof HTMLElement)) {
      console.warn('QuickAddDialog: Close button not found');
      return;
    }
    
    const button = /** @type {HTMLElement} */ (closeButton);

    // Set the declarative event handler attribute
    if (!button.hasAttribute('on:click')) {
      button.setAttribute('on:click', '/closeDialog');
    }

    // Remove any existing handler first
    if (button._closeHandler) {
      button.removeEventListener('click', button._closeHandler, { capture: true });
      button.removeEventListener('click', button._closeHandler);
      delete button._closeHandler;
    }

    // Add a direct event listener as a fallback to ensure it works
    // Use both capture and bubble phases to ensure it runs
    const clickHandler = async (/** @type {MouseEvent} */ event) => {
      console.log('QuickAddDialog: Close button clicked', event);
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      
      // Call closeDialog method
      try {
        // Get the correct dialog - should be in refs, but verify it's the quick-add-modal
        let dialog = this.refs?.dialog;
        
        // Verify we have the right dialog (quick-add-modal, not personalise-modal)
        if (dialog && !dialog.classList.contains('quick-add-modal')) {
          console.warn('QuickAddDialog: refs.dialog is wrong dialog, finding correct one');
          dialog = this.querySelector('dialog.quick-add-modal');
        }
        
        // If still no dialog, try to find it
        if (!dialog) {
          dialog = this.querySelector('dialog.quick-add-modal');
        }
        
        console.log('QuickAddDialog: Attempting to close dialog', {
          hasCloseDialog: typeof this.closeDialog === 'function',
          hasDialogRef: !!this.refs?.dialog,
          dialogElement: !!dialog,
          dialogOpen: dialog?.open,
          dialogClassList: dialog?.classList?.toString(),
          isQuickAddModal: dialog?.classList?.contains('quick-add-modal')
        });
        
        // If we don't have the right dialog, try to close it directly
        if (!dialog || !dialog.classList.contains('quick-add-modal')) {
          console.error('QuickAddDialog: Cannot find quick-add-modal dialog');
          return;
        }
        
        // If dialog is not open, something is wrong
        if (!dialog.open) {
          console.warn('QuickAddDialog: Dialog is not open, but trying to close anyway');
          // Still try to close it and reset styles
          dialog.close();
          this.#releaseScrollLock();
          return;
        }
        
        if (typeof this.closeDialog === 'function') {
          // closeDialog is async, so await it
          console.log('QuickAddDialog: Calling closeDialog method');
          await this.closeDialog();
          console.log('QuickAddDialog: closeDialog completed');
          
          // Double-check: if dialog is still open, force close it
          const checkDialog = this.refs?.dialog || this.querySelector('dialog.quick-add-modal');
          if (checkDialog && checkDialog.open) {
            console.warn('QuickAddDialog: Dialog still open after closeDialog, forcing close');
            checkDialog.close();
            // Also reset body styles in case closeDialog didn't complete properly
            this.#releaseScrollLock();
          }
        } else {
          console.warn('QuickAddDialog: closeDialog is not a function, using fallback');
          // Fallback: close dialog directly
          if (dialog.open) {
            dialog.close();
            // Reset body styles
            this.#releaseScrollLock();
            console.log('QuickAddDialog: Dialog close() called');
          }
        }
      } catch (error) {
        console.error('QuickAddDialog: Error closing dialog:', error, error.stack);
        // Last resort: try to close the quick-add dialog element directly
        try {
          const quickAddDialog = this.querySelector('dialog.quick-add-modal');
          if (quickAddDialog) {
            quickAddDialog.close();
            this.#releaseScrollLock();
            console.log('QuickAddDialog: Fallback - Quick-add dialog close() called directly');
          }
        } catch (fallbackError) {
          console.error('QuickAddDialog: Fallback close also failed:', fallbackError);
        }
      }
    };
    
    // Attach in both phases to ensure it works
    button.addEventListener('click', clickHandler, { capture: true });
    button.addEventListener('click', clickHandler);
    button.dataset.closeHandlerAttached = 'true';
    // @ts-ignore - storing handler reference for cleanup
    button._closeHandler = clickHandler;
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
